import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import BrandTitle from "../components/BrandTitle";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function Rails8PushNotifications() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Push Notifications on Rails 8, Expo & React Native: The TiiRADA Way",
        "July 30, 2026",
        "8min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title"><BrandTitle text={postTitle} /></h1>

            <p className="lead">
                Push notifications are the pulse of a marketplace app — a new bid, a message, a
                gig update. This post breaks down how TiiRADA wires them end to end: a Rails 8
                backend talking to Firebase Cloud Messaging, and a React Native / Expo client that
                renders the result. Everything below is real, taken straight from the codebase.
            </p>

            <h2 className="post-sect">The stack</h2>
            <p>
                Rails 8 with Ruby 3.4 on the backend, Solid Queue for background jobs (no
                Sidekiq), and a hand-rolled FCM client. Polling is deliberately tuned in{" "}
                <code>config/queue.yml</code>: development workers poll frequently for snappy local
                pickups, while production relaxes both dispatcher and workers to a few seconds — the jobs
                table lives on the shared Postgres, so tighter polling is idle database traffic
                you pay for. The mobile app runs on Expo (React Native) using{" "}
                <code>@react-native-firebase/messaging</code> for token registration and{" "}
                <code>@notifee/react-native</code> to render the banners on Android.
            </p>

            <h2 className="post-sect">Backend: registering the device token</h2>
            <p>
                Every user stores their device token on the <code>users</code> table in a dedicated{" "}
                <code>fcm_token</code> column. The app sends it to a registered device endpoint after every
                login:
            </p>
            <pre>
                <code className="language-bash">{`POST /api/v1/devices
Authorization: Bearer <auth-token>
{ "token": "<fcm-device-token>" }`}</code>
            </pre>
            <p>
                The controller does a few careful things in one transaction. It steals the token
                back from any previous owner first, so one device never pushes to two accounts:
            </p>
            <pre>
                <code className="language-ruby">{`# app/controllers/api/v1/devices_controller.rb
def update
  token = params[:token]
  return if token.blank?

  ActiveRecord::Base.transaction do
    # 1. Clear this token from any OTHER user (prevents shared-device leaks)
    User.where(fcm_token: token).where.not(id: current_user.id)
        .update_all(fcm_token: nil)
    # 2. Assign to the current user
    if current_user.update(fcm_token: token)
      render json: { message: 'Device token registered successfully.' }, status: :ok
    end
  end
end`}</code>
            </pre>
            <p>
                Logging out or closing the account clears the token too — the device stops receiving
                pushes the moment the session ends.
            </p>

            <h2 className="post-sect">Backend: the FCM service (no Firebase gem)</h2>
            <p>
                TiiRADA does not use <code>firebase-admin</code> or any Firebase Ruby SDK. The push
                layer is a compact HTTP client built on <code>httparty</code> that speaks the FCM
                HTTP v1 API directly. Authentication is an OAuth2 JWT minted from a Google service
                account key file:
            </p>
            <pre>
                <code className="language-ruby">{`# app/services/fcm_service.rb
class FcmService
  include HTTParty
  base_uri "https://fcm.googleapis.com/fcm"
  NOTIFICATION_CHANNEL_ID = "heads_up_channel_v1"

  def self.get_google_auth_info
    Rails.cache.fetch("fcm_google_auth_info", expires_in: 50.minutes) do
      require "googleauth"
      scopes = ["https://www.googleapis.com/auth/firebase.messaging"]
      key_path = ENV["GOOGLE_APPLICATION_CREDENTIALS"] || "config/credentials/fcm.json"
      key_data = JSON.parse(File.read(key_path))
      project_id = key_data["project_id"]
      authorizer = Google::Auth::ServiceAccountCredentials.make_creds(
        json_key_io: File.open(key_path),
        scope: scopes
      )
      token = authorizer.fetch_access_token!["access_token"]
      [token, project_id]
    end
  end

  def self.send_notification(token, title, message, data = {}, image_url = nil)
    project_id, auth_token = get_google_auth_info
    # ... build the payload and POST to
    # https://fcm.googleapis.com/v1/projects/<project_id>/messages:send
  end
end`}</code>
            </pre>
            <p>
                The access token is cached in <code>Rails.cache</code> for 50 minutes (the token is
                valid for about an hour), so the service account is only read once per window instead
                of on every push. The <code>key_path</code> is loaded through{" "}
                <code>GOOGLE_APPLICATION_CREDENTIALS</code> so the key never lives in the repository.
            </p>

            <h2 className="post-sect">One job to rule them all</h2>
            <p>
                Nearly every push funnels through a single <code>NotificationJob</code>. That job
                does four things, in order: decide the notification kind, persist the in-app
                notification row, send the FCM push, and broadcast over ActionCable:
            </p>
            <pre>
                <code className="language-ruby">{`# app/jobs/notification_job.rb
class NotificationJob < ApplicationJob
  queue_as :default

  def perform(user_id, title, message, resource_id = nil,
              resource_type = nil, image_url = nil, sender_id = nil)
    user = User.find_by(id: user_id)
    return unless user

    kind = Notification.kind_for(title: title, message: message,
                                 notifiable_type: resource_type)
    return unless user.notifications_enabled_for?(kind)   # per-kind opt-out

    # 1. Persist the in-app notification (unread, sender set)
    # 2. Generate a circular avatar variant URL -> largeIcon
    # 3. Skip the push if the user is currently active in that conversation

    if user.fcm_token.present? && !active_in_conversation?(resource_id)
      FcmService.send_notification(user.fcm_token, title, message, {
        resource_id: resource_id, resource_type: resource_type,
        kind: notification.kind, priority: notification.priority
      }, image_url)
    end

    # 4. Realtime refresh over ActionCable
    ActionCable.server.broadcast "notifications_#{user.id}", {
      type: "NEW_NOTIFICATION", refresh_badges: true, notification: { ... }
    }
  end
end`}</code>
            </pre>

            <h3 className="post-sect-sub">Who triggers it</h3>
            <p>
                The trigger points are spread across the domain, all using{" "}
                <code>perform_later</code> so Solid Queue owns the delivery:
            </p>
            <pre>
                <code className="language-ruby">{`# New bid  ->  PlaceBidService
NotificationJob.perform_later(client.id, "New Bid Received", ...,
                             job_post.id, "JobPost", nil, @tirador.id)

# New message  ->  Message#after_create_commit
NotificationJob.perform_later(recipient.id, "New Message from ...",
                             body.truncate(50), conversation_id,
                             "Conversation", nil, sender.id)

# Hired  ->  HireTiradorService
NotificationJob.perform_later(@bid.user_id, "TiiRADA Hire Alert!", ...,
                             job_post.id, "JobPost", ...)

# Gig expiring / expired  ->  ProcessStaleJobPostsJob (hourly, recurring.yml)
# Gig near you (geofence) ->  JobPostsController
# Report filed            ->  JobPostsController
# Reviews, security, profile reminders -> dedicated services/jobs`}</code>
            </pre>

            <h2 className="post-sect">Respecting the user&apos;s preferences</h2>
            <p>
                Every user gets a set of boolean columns —{" "}
                <code>notify_messages</code>, <code>notify_bids_hiring</code>,{" "}
                <code>notify_gig_alerts</code>, <code>notify_reviews</code>,{" "}
                <code>notify_reports_safety</code>, <code>notify_security</code> — all defaulting to
                true. The job maps a notification kind to one of those columns and bails early if
                the user has opted out of that category. No row, no push, no broadcast.
            </p>

            <h2 className="post-sect">The Android payload: data-only</h2>
            <p>
                Here is the interesting design choice. For Android, the FCM message is{" "}
                <strong>data-only</strong> — no <code>notification</code> block at all. That wakes
                the app&apos;s headless background handler, which applies TiiRADA&apos;s own channel,
                sound, and large icon instead of FCM&apos;s default rendering:
            </p>
            <pre>
                <code className="language-json">{`{
  "message": {
    "token": "<fcm-device-token>",
    "data": {
      "title": "New Bid Received",
      "body": "You have a new bid on your gig.",
      "resource_id": "42",
      "resource_type": "JobPost",
      "kind": "bid",
      "priority": "high",
      "channel_id": "tiirada_heads_up_v15",
      "image_url": "<avatar-proxy-url>"
    },
    "android": { "priority": "high" }
  }
}`}</code>
            </pre>
            <p>
                iOS is the opposite: Apple will not render data-only messages, so the same service
                includes an explicit <code>apns</code> alert block when the platform needs it.
            </p>

            <h2 className="post-sect">Mobile: registering and syncing the token</h2>
            <p>
                The whole client lives in one module. After login, the app requests permission and
                fetches the FCM token (with retries), stores it locally, then POSTs it to the
                backend:
            </p>
            <pre>
                <code className="language-typescript">{`// src/utils/notifications.ts
const authStatus = await requestPermission(getMessaging());
// iOS only: await registerDeviceForRemoteMessages(getMessaging())

const token = await getToken(getMessaging());   // up to 3 retries

await AsyncStorage.setItem('device_push_token', token);

// Only sync if it changed since last time (dedupe)
if (token !== last) {
  await axiosInstance.post('/devices', { token },
    { headers: { Authorization: \`Bearer \${authToken}\` } });
  await AsyncStorage.setItem('last_fcm_token', token);
}`}</code>
            </pre>
            <p>
                The Android channels are created up front so notifications render the way the app
                intends — a high-importance "heads up" channel with a custom sound, and a silent
                low-importance channel for events that already chimed:
            </p>
            <pre>
                <code className="language-typescript">{`const HEADS_UP_CHANNEL_ID = 'heads_up_channel_v1';
const SILENT_CHANNEL_ID   = 'silent_channel_v1';

// heads_up:  HIGH importance, badge, vibration [300,500],
//            light color, sound 'notification'
// silent:    LOW importance, no sound, no vibration`}</code>
            </pre>
            <p>
                Notice the channel id matches the one the backend bakes into every data payload —{" "}
                <code>heads_up_channel_v1</code>. That handshake is deliberate.
            </p>

            <h2 className="post-sect">Rendering the banner with Notifee</h2>
            <p>
                When a data-only push arrives in the background or from a killed state, the headless
                handler runs its guards and then calls <code>notifee.displayNotification</code> with
                the sender&apos;s avatar as a circular large icon:
            </p>
            <pre>
                <code className="language-typescript">{`// setBackgroundMessageHandler (Android background/killed state)
setBackgroundMessageHandler(getMessaging(), async (message) => {
  if (isDuplicate(message)) return;          // processed-ID dedupe
  if (isStale(message)) return;              // 10s freshness guard
  if (personaMismatch(message)) return;      // clients skip gig discovery
  if (isChimeDamped()) return;               // 3s cooldown vs ActionCable

  await displayAndroidPushNotification(message);
});

// displayAndroidPushNotification
await notifee.displayNotification({
  title,
  body,
  android: {
    channelId: HEADS_UP_CHANNEL_ID,
    smallIcon: 'ic_launcher',
    largeIcon: message.data.largeIcon || message.data.image_url,
    circularLargeIcon: true,
    color: Colors.palette.steel,
    sound: 'tiirada',
    pressAction: { id: 'default' },
  },
});`}</code>
            </pre>

            <h2 className="post-sect">Foreground: FCM and ActionCable both fire</h2>
            <p>
                Two engines can deliver the same event — FCM and a live ActionCable broadcast. To
                avoid double banners, the app uses a persistent list of processed message IDs, a
                10-second freshness check, and a 3-second chime cooldown. And when the user is
                actively reading the chat, the foreground push is dropped entirely:
            </p>
            <pre>
                <code className="language-typescript">{`// addNotificationListener (foreground onMessage)
addNotificationListener((message) => {
  if (global.activeChatId && isChatMessage(message)) return; // already looking
  if (isDuplicate(message)) return;
  if (isStale(message)) return;

  displayAndroidPushNotification(message);   // render our own banner
});`}</code>
            </pre>
            <p>
                Realtime conversation state also flows over ActionCable (Rails 8 ships Solid Cable)
                with the same guards, so badges and offers refresh live while the app is open.
            </p>

            <h2 className="post-sect">Tapping a notification navigates deep</h2>
            <p>
                The tap handler maps the payload to the right screen — a bid goes to the job
                details, a message to the conversation, a report to the report history:
            </p>
            <pre>
                <code className="language-typescript">{`// app/_layout.tsx
const resolvePushTarget = async (data) => {
  if (data.kind === 'report')        return '/settings/report-history';
  if (data.kind === 'security')      return '/settings/privacy';
  if (data.resourceType === 'profile') return '/profile/edit';
  if (data.resourceType === 'jobpost') return \`/job-details/\${data.resource_id}\`;
  if (data.resourceType === 'conversation') return \`/messages/\${data.resource_id}\`;
  return '/notifications';
};`}</code>
            </pre>
            <p>
                For a killed app, the pending route is stored in AsyncStorage, then consumed after
                the auth check on boot via <code>getInitialNotification</code> /{" "}
                <code>getInitialFcmNotification</code>.
            </p>

            <h2 className="post-sect">Lessons baked into the design</h2>
            <ul className="lead">
                <li><strong>No Firebase gem on the backend</strong> — a 50-line HTTP client speaks FCM v1 with a cached service-account token. Fewer dependencies, full control.</li>
                <li><strong>Data-only on Android</strong> — lets Notifee own channel, sound, icon, and color instead of the platform default.</li>
                <li><strong>Channel-id handshake</strong> — backend and app agree on <code>tiirada_heads_up_v15</code>; a silent twin channel covers already-chimed events.</li>
                <li><strong>Single-device hygiene</strong> — a token is stolen from any previous owner before assignment, and cleared on logout.</li>
                <li><strong>Presence-aware</strong> — no push while the user is looking at the conversation; no banner while reading the chat.</li>
                <li><strong>Anti-duplicate guards</strong> — processed IDs, freshness, and a chime cooldown keep FCM and ActionCable from double-firing.</li>
                <li><strong>Persona-aware filtering</strong> — clients ignore discovery-style gig alerts they shouldn&apos;t receive.</li>
            </ul>

            <h2 className="post-sect">Summary</h2>
            <p>
                Push notifications are an event pipeline, not a single call. The device registers a
                token after login; domain services enqueue one <code>NotificationJob</code> per
                event; the job persists an in-app row, sends a data-only FCM v1 message via a
                hand-rolled client, and broadcasts over ActionCable; and the mobile app renders it
                through Notifee with a custom channel, sound, and avatar — while guarding against
                duplicates and respecting the user&apos;s preferences at every step.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}